import { useState, useCallback } from 'react';
import useSWR from 'swr';
import { useAuth } from './useAuth';
import { useTenant } from './useTenant';
import { apiClient } from '@/api';

interface InviterInfo {
  id: string;
  name: string;
}

interface EmailStatus {
  sent: boolean;
  error?: string;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  invited_by: InviterInfo;
  expires_at: string;
  created_at: string;
  is_expired: boolean;
  email_status?: EmailStatus;
}

interface InvitationsResponse {
  invitations: Invitation[];
  total: number;
  has_more: boolean;
}

interface CreateInvitationRequest {
  email: string;
  role: string;
}

interface InvitationRevealResponse {
  email: string;
}

interface ValidateTokenResponse {
  valid: boolean;
  tenant_name?: string;
  role?: string;
  invited_by?: string;
  expires_at?: string;
  reason?: string;
}

interface AcceptInvitationResponse {
  tenant: {
    id: string;
    name: string;
    slug: string;
    subdomain_url: string;
  };
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
  };
}

interface UseInvitationsOptions {
  limit?: number;
  offset?: number;
}

interface UseInvitationsResult {
  invitations: Invitation[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  createInvitation: (request: CreateInvitationRequest) => Promise<Invitation>;
  cancelInvitation: (invitationId: string) => Promise<void>;
  resendInvitation: (invitationId: string) => Promise<Invitation>;
  revealInvitation: (invitationId: string) => Promise<InvitationRevealResponse>;
  isCreating: boolean;
  isCancelling: boolean;
  isResending: boolean;
}

export function useInvitations(options: UseInvitationsOptions = {}): UseInvitationsResult {
  const { session } = useAuth();
  const { tenant } = useTenant();
  const { limit = 50, offset = 0 } = options;
  const [isCreating, setIsCreating] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const queryParams = new URLSearchParams();
  if (limit) queryParams.set('limit', String(limit));
  if (offset) queryParams.set('offset', String(offset));

  const endpoint = `/api/v1/team/invitations?${queryParams.toString()}`;

  const fetcher = async (url: string): Promise<InvitationsResponse> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    return apiClient.get<InvitationsResponse>(url, { token: session.access_token });
  };

  const { data, error, isLoading, mutate } = useSWR<InvitationsResponse>(
    session?.access_token && tenant?.id ? endpoint : null,
    fetcher,
    {
      revalidateOnFocus: true,
      dedupingInterval: 5000,
    }
  );

  const refresh = async () => {
    await mutate();
  };

  const createInvitation = useCallback(
    async (request: CreateInvitationRequest): Promise<Invitation> => {
      if (!session?.access_token) {
        throw new Error('No authentication token');
      }
      setIsCreating(true);
      try {
        const response = await apiClient.post<Invitation>(
          '/api/v1/team/invitations',
          request,
          { token: session.access_token }
        );
        await mutate();
        return response;
      } finally {
        setIsCreating(false);
      }
    },
    [session?.access_token, mutate]
  );

  const cancelInvitation = useCallback(
    async (invitationId: string): Promise<void> => {
      if (!session?.access_token) {
        throw new Error('No authentication token');
      }
      setIsCancelling(true);
      try {
        await apiClient.delete(`/api/v1/team/invitations/${invitationId}`, {
          token: session.access_token,
        });
        await mutate();
      } finally {
        setIsCancelling(false);
      }
    },
    [session?.access_token, mutate]
  );

  const resendInvitation = useCallback(
    async (invitationId: string): Promise<Invitation> => {
      if (!session?.access_token) {
        throw new Error('No authentication token');
      }
      setIsResending(true);
      try {
        const response = await apiClient.post<Invitation>(
          `/api/v1/team/invitations/${invitationId}/resend`,
          {},
          { token: session.access_token }
        );
        await mutate();
        return response;
      } finally {
        setIsResending(false);
      }
    },
    [session?.access_token, mutate]
  );

  const revealInvitation = useCallback(
    async (invitationId: string): Promise<InvitationRevealResponse> => {
      if (!session?.access_token) {
        throw new Error('No authentication token');
      }
      return apiClient.get<InvitationRevealResponse>(
        `/api/v1/team/invitations/${invitationId}/reveal`,
        { token: session.access_token }
      );
    },
    [session?.access_token]
  );

  return {
    invitations: data?.invitations ?? [],
    total: data?.total ?? 0,
    hasMore: data?.has_more ?? false,
    isLoading,
    error: error ?? null,
    refresh,
    createInvitation,
    cancelInvitation,
    resendInvitation,
    revealInvitation,
    isCreating,
    isCancelling,
    isResending,
  };
}

// Hook for validating invitation tokens (used on public accept page)
export function useInvitationValidation(token: string | null) {
  const fetcher = async (url: string): Promise<ValidateTokenResponse> => {
    return apiClient.get<ValidateTokenResponse>(url);
  };

  const endpoint = token ? `/api/v1/invitations/validate?token=${encodeURIComponent(token)}` : null;

  const { data, error, isLoading } = useSWR<ValidateTokenResponse>(
    endpoint,
    fetcher,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  );

  return {
    validation: data,
    isLoading,
    error: error ?? null,
  };
}

// Function for accepting invitations (requires auth)
export async function acceptInvitation(
  token: string,
  accessToken: string
): Promise<AcceptInvitationResponse> {
  return apiClient.post<AcceptInvitationResponse>(
    '/api/v1/invitations/accept',
    { token },
    { token: accessToken }
  );
}

export type { Invitation, CreateInvitationRequest, ValidateTokenResponse, AcceptInvitationResponse, InvitationRevealResponse };
